import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    gatewayType: String,
    isFree: Boolean,

  };

  connect() {
    // Flag compartido de "pago en curso". Es la fuente de verdad del guard de
    // doble-submit: NO dependemos de button.disabled porque stripe-payment lo
    // re-habilita al terminar createPaymentMethod (ver doble cobro pi_3TsoUj...,
    // 13-jul-2026). Una vez true, ningún submit adicional se procesa hasta que
    // la página navegue (éxito/3DS) o se libere por error/validación.
    this.submitting = false;
    // Interceptar el submit del formulario para validar todo antes de enviar
    this.element.addEventListener("submit", this.handleSubmit.bind(this));
    // Inicializar referencias al botón
    this.initializeSubmitButton();
  }

  initializeSubmitButton() {
    // Obtener referencia al botón de submit para manejar turbo_submits_with
    this.submitButton = this.element.querySelector('button[type="submit"]');
    if (this.submitButton) {
      this.originalButtonText = this.submitButton.innerHTML;
      // Leer el texto de turbo_submits_with si existe (data-turbo-submits-with)
      this.submitsWithText = this.submitButton.dataset.turboSubmitsWith;
    }
  }

  disconnect() {
    this.element.removeEventListener("submit", this.handleSubmit.bind(this));
  }

  async handleSubmit(event) {
    // Si es gratis, dejar que el formulario se envíe normalmente
    if (this.isFreeValue) {
      return;
    }

    // Prevenir el submit para validar primero
    event.preventDefault();
    event.stopPropagation();

    // Guard de doble-submit: si ya hay un pago en curso, ignorar el submit.
    // Immune a que otro controller re-habilite el botón (ver connect()).
    if (this.submitting) {
      return;
    }
    this.submitting = true;

    // Aplicar turbo_submits_with manualmente
    this.disableSubmitButton();

    try {
      // ORDEN DE VALIDACIÓN:
      // 1. PRIMERO: Validar el formulario principal (usuario, DTE, etc.)
      if (!this.validateForm()) {
        this.releaseSubmitLock();
        return;
      }

      // 2. DESPUÉS: Leer el gateway_type y validar el gateway (Stripe, etc.)
      const gatewayType = this.getGatewayType();

      if (gatewayType === "stripe") {
        const stripeValid = await this.validateStripe();
        if (!stripeValid) {
          this.releaseSubmitLock();
          return;
        }
      }

      // 3. Todo validado: iniciar el pago. A partir de aquí NO liberamos el
      // lock salvo que el server rechace el submit (submitForm lo maneja).
      await this.submitForm();
    } catch (error) {
      console.error("[CheckoutFormController] Error in handleSubmit:", error);
      this.releaseSubmitLock();
    }
  }

  // Valida el formulario principal usando HTML5 validation
  validateForm() {
    if (!this.element.checkValidity()) {
      this.element.reportValidity();
      return false;
    }
    return true;
  }

  // Obtiene el gateway_type del formulario
  getGatewayType() {
    const gatewayTypeInput = this.element.querySelector(
      'input[name="showcase_checkout_checkout_form[sale_params][gateway_type]"]'
    );
    return gatewayTypeInput ? gatewayTypeInput.value : this.gatewayTypeValue;
  }

  // Valida Stripe llamando al controlador de Stripe
  async validateStripe() {
    const stripeElement = document.querySelector(
      '[data-controller*="stripe-payment"]'
    );

    if (!stripeElement) {
      this.showError("No se encontró el formulario de pago de Stripe");
      return false;
    }

    // Obtener el controlador de Stripe
    const stripeController =
      this.application.getControllerForElementAndIdentifier(
        stripeElement,
        "stripe-payment"
      );

    if (!stripeController) {
      this.showError("Error al acceder al controlador de Stripe");
      return false;
    }

    try {
      const result = await stripeController.createPaymentMethod();
      return result;
    } catch (error) {
      console.error("[CheckoutFormController] Error validating Stripe:", error);
      this.showError("Error al validar el pago de Stripe");
      return false;
    }
  }

  // Envía el formulario
  async submitForm() {
    const formData = new FormData(this.element);

    try {
      const response = await fetch(this.element.action, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "text/vnd.turbo-stream.html, text/html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
      });

      if (response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("turbo-stream")) {
          const html = await response.text();
          Turbo.renderStreamMessage(html);
        } else {
          const redirectUrl = response.url;
          if (redirectUrl) {
            window.location.href = redirectUrl;
          }
        }
        // Éxito / 3DS / redirect: el pago sigue en curso, mantenemos el lock.
      } else {
        // El server rechazó el submit (errores de formulario): liberamos el
        // lock para que el usuario pueda corregir y reintentar.
        const html = await response.text();
        Turbo.renderStreamMessage(html);
        this.showError("Por favor, corrige los errores en el formulario");
        this.releaseSubmitLock();
      }
    } catch (error) {
      console.error("[CheckoutFormController] Error submitting form:", error);
      this.showError(
        "Error al enviar el formulario. Por favor, intenta nuevamente."
      );
      this.releaseSubmitLock();
    }
  }

  disableSubmitButton() {
    // Asegurarse de que tenemos la referencia al botón
    if (!this.submitButton) {
      this.initializeSubmitButton();
    }
    if (this.submitButton) {
      this.submitButton.disabled = true;
      if (this.submitsWithText) {
        this.submitButton.innerHTML = this.submitsWithText;
      }
    }
  }

  // Libera el guard de doble-submit y re-habilita el botón. Solo se llama
  // cuando el pago NO llegó a iniciarse (validación fallida) o el server
  // rechazó el submit. Nunca tras iniciar un pago exitoso / 3DS.
  releaseSubmitLock() {
    this.submitting = false;
    this.enableSubmitButton();
  }

  enableSubmitButton() {
    // Asegurarse de que tenemos la referencia al botón
    if (!this.submitButton) {
      this.initializeSubmitButton();
    }
    if (this.submitButton && this.originalButtonText) {
      this.submitButton.disabled = false;
      this.submitButton.innerHTML = this.originalButtonText;
    }
  }

  showError(message) {
    console.error(message);
  }
}
